import { FormProps } from 'types/form';
import { DeliveryZonePlaceConfig, Place } from '../types/dtos';
import { ResponseTerminalDto } from 'features/terminal/types/dtos';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import OWForm from 'components/layout/ow-form';
import { Autocomplete, Button, Divider, Stack, TextField, Typography } from '@mui/material';
import LogoDropZone from 'components/controls/logo-drop-zone';
import FormFooter from 'components/layout/form-footer';
import { LoadingButton } from '@mui/lab';
import { COUNTRIES } from '../constants/place-fields';
import { ResponseCityDto } from 'features/city/types/dtos';
import { WorkingHoursAccordion } from './working-hours-accordion';
import { DeliveryZonePriceAccordion } from './delivery-zone-prices-accordion';

export type PlaceFormProps = FormProps<Place> & {
    terminals?: { content: ResponseTerminalDto[] };
    website: { id: string; name: string };
    cities?: { content: ResponseCityDto[] };
    isAdmin?: boolean;
    deliveryZonePlaceConfig?: DeliveryZonePlaceConfig[];
    onDeliveryPriceChange?: (id: string, newValue: string) => void;
};

type PlaceFormType = (props: PlaceFormProps) => ReactNode;

export const PlaceForm: PlaceFormType = ({
    formData: place,
    terminals,
    website,
    cities,
    onChange,
    onSubmit,
    onCancel,
    isSaveLoading,
    disableSubmit,
    errors,
    isAdmin = true,
    deliveryZonePlaceConfig,
    onDeliveryPriceChange,
}) => {
    const { t } = useTranslation();

    return (
        <OWForm onSubmit={onSubmit}>
            <Typography variant="h6">{t('PLACE_FORM.HEADER')}</Typography>
            <Stack direction="row" spacing={1} alignItems="flex-start">
                <Stack width="100%" spacing={1.25}>
                    <TextField
                        value={place.name}
                        onChange={(e) => onChange('name', e.target.value)}
                        placeholder={t('PLACE_FORM.PLACEHOLDER_NAME')}
                        label={t('PLACE_FORM.LABEL_NAME')}
                        required
                        autoFocus
                        error={!!errors?.name}
                        helperText={errors?.name?.errorMessage}
                        disabled={!isAdmin}
                    />
                    <TextField
                        value={place.minimumOrderAmount}
                        onChange={(e) => onChange('minimumOrderAmount', e.target.value)}
                        placeholder={t('PLACE_FORM.PLACEHOLDER_MIN_ORDER_AMOUNT')}
                        label={t('PLACE_FORM.LABEL_MIN_ORDER_AMOUNT')}
                        required
                        error={!!errors?.minimumOrderAmount}
                        helperText={errors?.minimumOrderAmount?.errorMessage}
                        disabled={!isAdmin}
                    />
                </Stack>
                <LogoDropZone logo={place.logo} onChange={(value) => onChange('logo', value)} />
            </Stack>
            <WorkingHoursAccordion place={place} onChange={onChange} />
            {deliveryZonePlaceConfig !== undefined && onDeliveryPriceChange !== undefined && (
                <DeliveryZonePriceAccordion
                    deliveryZonePlaceConfig={deliveryZonePlaceConfig}
                    onChange={onDeliveryPriceChange}
                    errors={errors}
                />
            )}
            <Stack spacing={1}>
                <Autocomplete
                    options={terminals?.content || []}
                    value={terminals?.content.find((terminal) => terminal.id === place.terminalId) || null}
                    getOptionLabel={(option: { id: string; name: string }) => option.name}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t('PLACE_FORM.LABEL_TERMINAL')}
                            required
                            error={!!errors?.terminalId}
                            helperText={errors?.terminalId?.errorMessage}
                        />
                    )}
                    onChange={(_, value) => onChange('terminalId', value?.id)}
                    disabled={!isAdmin}
                />
                <Autocomplete
                    options={[website]}
                    value={website}
                    getOptionLabel={(option: { id: string; name: string }) => option.name}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t('PLACE_FORM.LABEL_WEBSITE')}
                            required
                            error={!!errors?.websiteId}
                            helperText={errors?.websiteId?.errorMessage}
                        />
                    )}
                    disabled={true}
                    onChange={(_, value) => onChange('websiteId', value?.id)}
                />
            </Stack>
            <Divider variant="middle" />
            <Stack spacing={2}>
                <Autocomplete
                    options={Object.values(COUNTRIES)}
                    value={place.country}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            required
                            error={!!errors?.country}
                            helperText={errors?.country?.errorMessage}
                            label={t('PLACE_FORM.LABEL_COUNTRY')}
                        />
                    )}
                    onChange={(_, value) => onChange('country', value)}
                    disabled={!isAdmin}
                />
                <Stack direction="row" spacing={1}>
                    <Autocomplete
                        options={cities?.content || []}
                        value={cities?.content.find((city) => city.id === place.cityId) || null}
                        getOptionLabel={(option: { id: string; name: string }) => option.name}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t('PLACE_FORM.LABEL_CITY')}
                                required
                                helperText={errors?.cityId?.errorMessage}
                            />
                        )}
                        onChange={(_, value) => onChange('cityId', value?.id)}
                        fullWidth
                        disabled={!isAdmin}
                    />
                    <TextField
                        value={place.zipCode}
                        onChange={(e) => onChange('zipCode', e.target.value)}
                        placeholder={t('PLACE_FORM.PLACEHOLDER_ZIP_CODE')}
                        label={t('PLACE_FORM.LABEL_ZIP_CODE')}
                        required
                        error={!!errors?.zipCode}
                        helperText={errors?.zipCode?.errorMessage}
                        fullWidth
                        disabled={!isAdmin}
                    />
                </Stack>
                <Stack direction="row" spacing={1}>
                    <TextField
                        value={place.street}
                        onChange={(e) => onChange('street', e.target.value)}
                        placeholder={t('PLACE_FORM.PLACEHOLDER_STREET')}
                        label={t('PLACE_FORM.LABEL_STREET')}
                        required
                        error={!!errors?.street}
                        helperText={errors?.street?.errorMessage}
                        fullWidth
                        disabled={!isAdmin}
                    />
                    <TextField
                        value={place.streetNumber}
                        onChange={(e) => onChange('streetNumber', e.target.value)}
                        placeholder={t('PLACE_FORM.PLACEHOLDER_STREET_NUMBER')}
                        label={t('PLACE_FORM.LABEL_STREET_NUMBER')}
                        error={!!errors?.streetNumber}
                        helperText={errors?.streetNumber?.errorMessage}
                        required
                        fullWidth
                        disabled={!isAdmin}
                    />
                </Stack>
                <Stack direction="row" spacing={1}>
                    <TextField
                        value={place.floor}
                        onChange={(e) => onChange('floor', e.target.value)}
                        placeholder={t('PLACE_FORM.PLACEHOLDER_FLOOR')}
                        label={t('PLACE_FORM.LABEL_FLOOR')}
                        error={!!errors?.floor}
                        helperText={errors?.floor?.errorMessage}
                        fullWidth
                        disabled={!isAdmin}
                    />
                    <TextField
                        value={place.apartment}
                        onChange={(e) => onChange('apartment', e.target.value)}
                        placeholder={t('PLACE_FORM.PLACEHOLDER_APARTMENT')}
                        label={t('PLACE_FORM.LABEL_APARTMENT')}
                        error={!!errors?.apartment}
                        helperText={errors?.apartment?.errorMessage}
                        fullWidth
                        disabled={!isAdmin}
                    />
                </Stack>
                <TextField
                    value={place.phoneNumber}
                    onChange={(e) => onChange('phoneNumber', e.target.value)}
                    placeholder={t('PLACE_FORM.PLACEHOLDER_PHONE_NUMBER')}
                    label={t('PLACE_FORM.LABEL_PHONE_NUMBER')}
                    required
                    error={!!errors?.phoneNumber}
                    helperText={errors?.phoneNumber?.errorMessage}
                    fullWidth
                />
                <TextField
                    value={place.description}
                    onChange={(e) => onChange('description', e.target.value)}
                    placeholder={t('PLACE_FORM.PLACEHOLDER_DESCRIPTION')}
                    label={t('PLACE_FORM.LABEL_DESCRIPTION')}
                    type="text"
                    error={!!errors?.description}
                    helperText={errors?.description?.errorMessage}
                    multiline
                    minRows={3}
                />
            </Stack>
            <Divider variant="middle" />
            <Stack spacing={2}>
                <TextField
                    id="longitude"
                    value={place.longitude}
                    onChange={(e) => onChange('longitude', e.target.value)}
                    placeholder={t('PLACE_FORM.PLACEHOLDER_LONGITUDE')}
                    label={t('PLACE_FORM.LABEL_LONGITUDE')}
                    error={!!errors?.longitude}
                    helperText={errors?.longitude?.errorMessage}
                    required
                    fullWidth
                    disabled={!isAdmin}
                />
                <TextField
                    id="latitude"
                    value={place.latitude}
                    onChange={(e) => onChange('latitude', e.target.value)}
                    placeholder={t('PLACE_FORM.PLACEHOLDER_LATITUDE')}
                    label={t('PLACE_FORM.LABEL_LATITUDE')}
                    error={!!errors?.latitude}
                    helperText={errors?.latitude?.errorMessage}
                    required
                    fullWidth
                    disabled={!isAdmin}
                />
            </Stack>
            <FormFooter>
                <Stack direction="row" width="360px" spacing={3}>
                    <Button variant="outlined" onClick={onCancel}>
                        {t('PLACE_FORM.CANCEL')}
                    </Button>
                    <LoadingButton loading={isSaveLoading} type="submit" disabled={disableSubmit}>
                        {t('PLACE_FORM.SUBMIT')}
                    </LoadingButton>
                </Stack>
            </FormFooter>
        </OWForm>
    );
};
